import {ApplicationRef, ComponentFactoryResolver, Injectable, Injector, NgModule, Type} from '@angular/core';
import {DefaultUrlSerializer, Route, Routes} from '@angular/router';
import {RouteEx} from './route.ex';

@Injectable({
  providedIn: 'root'
})
export class RouteSolverService {

  private serializer = new DefaultUrlSerializer();

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private injector: Injector,
              private appRef: ApplicationRef) {
  }

  // tslint:disable-next-line:no-unnecessary-initializer
  async findRoute(appRoutes: Routes, splits: string[], currentIndex: number, currentIcon: string | undefined = undefined): Promise<RouteEx> {
    if (currentIndex >= splits.length) {
      return null;
    }

    const currentPath = splits[currentIndex];
    const orderedRoutes = this.orderRoutes(appRoutes);
    const route = this.matchRoute(appRoutes, currentPath);

    if (!route) {
      return null;
    }

    const icon = route.data?.icon;
    if (icon) {
      currentIcon = icon;
    }

    if (route.loadChildren) {
      const lazyLoadedRoutes = await this.loadLazyLoadedRoutes(route);
      return this.findRoute(lazyLoadedRoutes, splits, currentIndex, currentIcon);
    }

    if (currentIndex === splits.length - 1) {
      const routeEx = route as RouteEx;
      routeEx.icon = currentIcon;
      return routeEx;
    }

    if (route.children) {
      return this.findRoute(route.children, splits, currentIndex + 1, currentIcon);
    }

    return null;
  }

  private matchRoute(appRoutes: Routes, currentPath: string): Route {
    return appRoutes.find(route => {
      if (route.path === currentPath || route.path === '' || route.path.startsWith(':')) {
        return true;
      }
      return false;
    });
  }

  private async loadLazyLoadedRoutes(route: Route): Promise<Routes> {
    const loadChildren = route.loadChildren as () => Promise<Type<NgModule>>;
    const module = await loadChildren() as any;
    const ctor = module.prototype.constructor;
    const instance = new ctor();
    const onLoadMethod = instance.onModuleLoad;
    if (onLoadMethod) {
      await onLoadMethod(this.componentFactoryResolver, this.injector, this.appRef);
    }
    return instance.routes;
  }

  orderRoutes(routes: Routes): Routes {
    return routes.sort((routeA, routeB) => {
      const specificityA = this.getRouteSpecificity(routeA);
      const specificityB = this.getRouteSpecificity(routeB);
      return specificityA - specificityB;
    });
  }

  private getRouteSpecificity(route: Route): number {
    if (!route.path) {
      return 0;
    }

    const segments = route.path.split('/');

    let specificity = 0;
    for (const segment of segments) {
      if (segment.startsWith(':')) {
        specificity += 100;
      } else if (segment === '**') {
        specificity += 1000;
      } else {
        specificity += 1;
      }
    }

    return specificity;
  }
}

